import type { ToolDefinition } from './types';
import { targetSizeTemplate, compressionSynonymTemplate, useCaseTemplate, cropTemplate, rotateTemplate, resizePercentTemplate, batchTemplate, adjustTemplate, imagePdfTemplate } from './templates';
import {
  phase1TargetSizeConfigs,
  phase1CompressionConfigs,
  phase1UseCaseConfigs,
  phase2CropConfigs,
  phase2RotateConfigs,
  phase2ResizePercentConfigs,
  phase2BatchConfigs,
  phase3AdjustConfigs,
  phase3PdfConfigs,
  phase3AdvancedConvertConfigs,
  type TargetSizeConfig,
  type CompressionConfig,
  type UseCaseConfig,
  type CropConfig,
  type RotateConfig,
  type ResizePercentConfig,
  type BatchConfig,
  type AdjustConfig,
  type PdfConfig,
  type AdvancedConvertConfig,
} from './configs';

function generateTargetSizeTool(config: TargetSizeConfig): ToolDefinition {
  const content = targetSizeTemplate(config.size, config.keyword);
  return {
    ...content,
    slug: config.slug,
    pageType: 'target-size',
    searchIntent: 'reduce-size',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'target-size',
    targetSize: config.size,
    relatedSlugs: [],
  };
}

function generateCompressionTool(config: CompressionConfig): ToolDefinition {
  const content = compressionSynonymTemplate(config.keyword, config.displayKeyword);
  return {
    ...content,
    slug: config.slug,
    pageType: 'compression',
    searchIntent: 'compress',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'compression',
    relatedSlugs: [],
  };
}

function generateUseCaseTool(config: UseCaseConfig): ToolDefinition {
  const content = useCaseTemplate(config.useCase, config.context, config.detailText);
  return {
    ...content,
    slug: config.slug,
    pageType: config.pageType,
    searchIntent: config.pageType === 'optimization' ? 'optimize' : 'compress',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'compression',
    relatedSlugs: [],
  };
}

function generateCropTool(config: CropConfig): ToolDefinition {
  const content = cropTemplate(config.aspectRatio, config.platform);
  return {
    ...content,
    slug: config.slug,
    pageType: 'crop',
    searchIntent: 'crop',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'crop',
    aspectRatio: config.aspectRatio,
    platform: config.platform,
    relatedSlugs: [],
  };
}

function generateRotateTool(config: RotateConfig): ToolDefinition {
  const content = rotateTemplate(config.keyword, config.displayKeyword, config.rotatePreset);
  return {
    ...content,
    slug: config.slug,
    pageType: 'rotate',
    searchIntent: 'rotate',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'rotate',
    rotatePreset: config.rotatePreset,
    relatedSlugs: [],
  };
}

function generateResizePercentTool(config: ResizePercentConfig): ToolDefinition {
  const content = resizePercentTemplate(config.percent, config.displayKeyword);
  return {
    ...content,
    slug: config.slug,
    pageType: 'resize-percent',
    searchIntent: 'resize',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'resize-percent',
    resizePercent: config.percent,
    relatedSlugs: [],
  };
}

function generateBatchTool(config: BatchConfig): ToolDefinition {
  const content = batchTemplate(config.displayKeyword, 'comprime');
  return {
    ...content,
    slug: config.slug,
    pageType: 'batch',
    searchIntent: 'batch',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'batch',
    batchToolType: config.batchToolType,
    relatedSlugs: [],
  };
}

function generateAdjustTool(config: AdjustConfig): ToolDefinition {
  const content = adjustTemplate(config.preset, config.displayKeyword);
  return {
    ...content,
    slug: config.slug,
    pageType: 'adjust',
    searchIntent: 'adjust',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'adjust',
    adjustPreset: config.preset,
    relatedSlugs: [],
  };
}

function generatePdfTool(config: PdfConfig): ToolDefinition {
  const content = imagePdfTemplate(config.displayKeyword);
  return {
    ...content,
    slug: config.slug,
    pageType: 'image-to-pdf',
    searchIntent: 'convert-pdf',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'image-to-pdf',
    pdfMode: 'image-to-pdf',
    relatedSlugs: [],
  };
}

function generateAdvancedConvertTool(config: AdvancedConvertConfig): ToolDefinition {
  const displayKeyword = `convertir ${config.sourceFormat} a ${config.targetFormat}`;
  const content = compressionSynonymTemplate(config.slug, displayKeyword);
  return {
    ...content,
    slug: config.slug,
    pageType: 'conversion',
    searchIntent: 'convert',
    schemaType: 'SoftwareApplication',
    canonicalPath: `/herramientas/${config.slug}`,
    toolExampleType: 'conversion',
    sourceFormat: config.sourceFormat,
    targetFormat: config.targetFormat,
    relatedSlugs: [],
  };
}

export function generatePhase1Tools(): ToolDefinition[] {
  return [
    ...phase1TargetSizeConfigs.map(generateTargetSizeTool),
    ...phase1CompressionConfigs.map(generateCompressionTool),
    ...phase1UseCaseConfigs.map(generateUseCaseTool),
  ];
}

export function generatePhase2Tools(): ToolDefinition[] {
  return [
    ...phase2CropConfigs.map(generateCropTool),
    ...phase2RotateConfigs.map(generateRotateTool),
    ...phase2ResizePercentConfigs.map(generateResizePercentTool),
    ...phase2BatchConfigs.map(generateBatchTool),
  ];
}

export function generatePhase3Tools(): ToolDefinition[] {
  return [
    ...phase3AdjustConfigs.map(generateAdjustTool),
    ...phase3PdfConfigs.map(generatePdfTool),
    ...phase3AdvancedConvertConfigs.map(generateAdvancedConvertTool),
  ];
}
